import { TextInput, Button } from '@mantine/core';
import { Select, toastNotifications, variants, Modal } from '@uag/react-core';
import {
    useCreateInvitationForExistingCustomer,
    useSendDoka360InvitationForExistingCustomer,
} from '@uag/welcome-shared/src/api/queries/invitations/invitations';
import { CustomerNumberField } from '@uag/welcome-shared/src/components/customer/CustomerNumberField';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { create, InstanceProps } from 'react-modal-promise';

import { invitationSchema, InvitationFormValues } from './invitationSchema';

const getLanguageDisplayName = (code: string): string => {
    try {
        const displayNames = new Intl.DisplayNames([code], { type: 'language' });
        const name = displayNames.of(code);
        if (name) {
            return name.charAt(0).toUpperCase() + name.slice(1);
        }
    } catch {
        // fallback
    }
    return code;
};

const invitationLanguageOptions = ['en', 'de', 'es'].map((code) => ({
    value: code,
    label: getLanguageDisplayName(code),
}));

const initialValues: InvitationFormValues = {
    customerIdentifier: '',
    companyName: '',
    companyRegistrarEmail: '',
    companyRegistrarFirstName: '',
    companyRegistrarLastName: '',
    invitationLanguage: 'en',
};

type Props = InstanceProps<{ requestId?: string }>;

const InviteCompanyModal = ({ isOpen, onResolve, onReject }: Props) => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [values, setValues] = useState<InvitationFormValues>(initialValues);
    const [isCountrySupportedInDoka360, setIsCountrySupportedInDoka360] = useState(false);

    const { mutateAsync: inviteExistingCustomer } = useCreateInvitationForExistingCustomer();
    const { mutateAsync: sendDoka360Invitation } = useSendDoka360InvitationForExistingCustomer();

    const submit = () => onResolve({});
    const reject = () => onReject();

    const setValue = <TKey extends keyof InvitationFormValues>(key: TKey, value: InvitationFormValues[TKey]) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    };

    const isValid =
        invitationSchema.safeParse(values).success && (!isCountrySupportedInDoka360 || !!values.invitationLanguage);

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            const invitee = {
                model: {
                    email: values.companyRegistrarEmail,
                    firstName: values.companyRegistrarFirstName || undefined,
                    lastName: values.companyRegistrarLastName || undefined,
                },
            };

            if (isCountrySupportedInDoka360) {
                await sendDoka360Invitation({
                    data: {
                        customerIdentifier: values.customerIdentifier,
                        companyName: values.companyName,
                        invitee,
                        invitationLanguage: values.invitationLanguage ?? 'en',
                    },
                });
            } else {
                await inviteExistingCustomer({
                    data: {
                        customerIdentifier: values.customerIdentifier,
                        companyName: values.companyName,
                        invitee,
                    },
                });
            }
            toastNotifications.success({
                title: t('success'),
                message: t('invited', { data: t('company') }),
            });
            submit();
        } catch (error) {
            toastNotifications.errorDetail(
                {
                    title: t('failed'),
                    message: t('notInvited', { data: t('company') }),
                },
                error,
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal opened={isOpen} size="50rem" title={t('inviteCompanyData')} onAbort={reject} onClose={reject}>
            <div className="gap-base flex w-full flex-col">
                <CustomerNumberField
                    initialCustomerNumber={values.customerIdentifier}
                    onCustomerNumberChanged={(value, doka360Supported) => {
                        setValue('customerIdentifier', value);
                        setIsCountrySupportedInDoka360(doka360Supported);
                    }}
                />
                <TextInput
                    label={t('companyName')}
                    value={values.companyName}
                    required
                    onChange={(ev) => setValue('companyName', ev.target.value)}
                />
                <TextInput
                    description={t('companyRegistrarDescription')}
                    label={t('companyRegistrarEmail')}
                    value={values.companyRegistrarEmail}
                    required
                    onChange={(ev) => setValue('companyRegistrarEmail', ev.target.value)}
                />
                <TextInput
                    label={t('companyRegistrarFirstName')}
                    value={values.companyRegistrarFirstName}
                    onChange={(ev) => setValue('companyRegistrarFirstName', ev.target.value)}
                />
                <TextInput
                    label={t('companyRegistrarLastName')}
                    value={values.companyRegistrarLastName}
                    onChange={(ev) => setValue('companyRegistrarLastName', ev.target.value)}
                />
                {isCountrySupportedInDoka360 && (
                    <Select
                        data={invitationLanguageOptions}
                        label={t('invitationLanguage')}
                        value={values.invitationLanguage}
                        onChange={(value) => setValue('invitationLanguage', value ?? 'en')}
                    />
                )}
            </div>

            <Modal.Footer>
                <Button className="modal-action" variant={variants.button.secondary} onClick={reject}>
                    {t('cancel')}
                </Button>
                <Button className="modal-action" disabled={!isValid} loading={isLoading} onClick={handleSubmit}>
                    {t('sendInvitation')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const openInviteCompanyModal = create(InviteCompanyModal);
